import { FC, useState } from 'react';

import { Box, CircularProgress } from '@mui/material';

import { ConfirmationDialog, FilePreviewModal } from '@/components';
import { Button } from '@/components/TailwindComponents/base/Button';
import { SidePaneModal } from '@/components/TailwindComponents/components/Modals/SidePaneModal';
import { IFile } from '@/models';
import { MasterIndexType } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';

import { MergeConflictStep } from './MergeConflictStep';
import { mergeConfigs } from './mergeFieldConfig';
import { MergeSearchStep } from './MergeSearchStep';
import { useMergeTool } from './useMergeTool';

interface MergeToolPanelProps {
  open: boolean;
  onClose: () => void;
  masterIndexType: MasterIndexType;
  currentEntityId: string | undefined;
  onMergeComplete: () => void;
}

export const MergeToolPanel: FC<MergeToolPanelProps> = ({
  open,
  onClose,
  masterIndexType,
  currentEntityId,
  onMergeComplete,
}) => {
  const {
    step,
    currentRecord,
    searchQuery,
    setSearchQuery,
    onSearchFocus,
    searchResults,
    searchLoading,
    selectedRecord,
    conflicts,
    choices,
    setChoice,
    selectAll,
    deselectRecord,
    selectedLoading,
    selectRecord,
    showConfirmDialog,
    setShowConfirmDialog,
    executeMerge,
    merging,
    sourceNotes,
    noteChecks,
    toggleNote,
    sourceCautions,
    cautionChecks,
    toggleCaution,
    currentNotes,
    currentCautions,
    currentFlags,
    sourceFlags,
    flagChecks,
    toggleFlag,
    currentRelatedRecords,
    relatedRecords,
    currentSystemFlags,
    sourceSystemFlags,
    prePlanHasOtherAgencyData,
  } = useMergeTool(masterIndexType, currentEntityId, open, onMergeComplete);

  const [previewFile, setPreviewFile] = useState<
    { file: IFile; sasToken: string | undefined } | undefined
  >(undefined);

  const config = mergeConfigs[masterIndexType];

  const handleMergeClick = () => {
    if (step === 'conflict') {
      setShowConfirmDialog(true);
    }
  };

  const currentTitle = currentRecord
    ? config.getRecordTitle(currentRecord)
    : currentEntityId ?? '';
  const selectedTitle = selectedRecord
    ? config.getRecordTitle(selectedRecord)
    : '';

  const confirmDescription =
    `This action will merge ${selectedTitle} into ${currentTitle}.\n\n` +
    `All selected data from ${selectedTitle} will be merged into ${currentTitle}. ` +
    `After completion, only ${currentTitle} will be retained.\n\n` +
    `This action cannot be undone.`;

  return (
    <>
      <SidePaneModal
        title="Merge Tool"
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
        disableClickAway
        modal={false}
        className="w-[700px] sm:w-[700px]"
      >
        <SidePaneModal.Content>
          {selectedLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          {!selectedLoading && step === 'search' && (
            <MergeSearchStep
              masterIndexType={masterIndexType}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSearchFocus={onSearchFocus}
              searchResults={searchResults}
              searchLoading={searchLoading}
              onSelect={selectRecord}
            />
          )}
          {!selectedLoading && step === 'conflict' && selectedRecord && (
            <MergeConflictStep
              masterIndexType={masterIndexType}
              selectedRecord={selectedRecord}
              conflicts={conflicts}
              choices={choices}
              onSetChoice={setChoice}
              onSelectAll={selectAll}
              onDeselect={deselectRecord}
              onPreviewFile={(file, sasToken) =>
                setPreviewFile({ file, sasToken })
              }
              sourceNotes={sourceNotes}
              noteChecks={noteChecks}
              onToggleNote={toggleNote}
              sourceCautions={sourceCautions}
              cautionChecks={cautionChecks}
              onToggleCaution={toggleCaution}
              currentNotes={currentNotes}
              currentCautions={currentCautions}
              currentFlags={currentFlags}
              sourceFlags={sourceFlags}
              flagChecks={flagChecks}
              onToggleFlag={toggleFlag}
              currentRelatedRecords={currentRelatedRecords}
              relatedRecords={relatedRecords}
              currentSystemFlags={currentSystemFlags}
              sourceSystemFlags={sourceSystemFlags}
              prePlanHasOtherAgencyData={prePlanHasOtherAgencyData}
            />
          )}
        </SidePaneModal.Content>

        <SidePaneModal.Footer>
          <Button variant="outline" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!selectedRecord || merging}
            onClick={handleMergeClick}
          >
            {merging ? 'Merging...' : 'Merge'}
          </Button>
        </SidePaneModal.Footer>
      </SidePaneModal>

      {/* Rendered outside SidePaneModal so it appears above the panel */}
      <FilePreviewModal
        open={Boolean(previewFile)}
        file={previewFile?.file}
        sasToken={previewFile?.sasToken}
        onClose={() => setPreviewFile(undefined)}
        sx={{ zIndex: 3000 }}
      />
      <ConfirmationDialog
        title="Are you sure that you want to apply changes?"
        description={confirmDescription}
        confirmText="Apply"
        cancelText="Cancel"
        preWrapWhiteSpace
        open={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={executeMerge}
        sx={{ zIndex: 3000 }}
      />
    </>
  );
};
